<script setup lang="ts">
import { computed } from 'vue';
import * as Locale from '@/plugins/i18n';
import { useLiveChatIndicatorCustomisation } from './useLiveChatIndicatorCustomisation';
import * as SUtils from '@/common/customisation/utils/styleUtils';

export interface LiveChatIndicatorProps {
  disabled?: boolean;
}

const props = withDefaults(defineProps<LiveChatIndicatorProps>(), {
  disabled: false,
});

const emit = defineEmits<{
  (e: 'end-chat'): void;
}>();

const customisation = useLiveChatIndicatorCustomisation();

const text = computed(() => {
  return customisation.textContent.value || (Locale.i18n.t('chat.live_chat.in_progress') as string);
});

const containerStyle = computed(() => {
  return SUtils.combineStyles(
    SUtils.createBackgroundStyle(customisation.backgroundColor),
    SUtils.createBorderStyle(customisation.borderColor)
  );
});

const iconStyle = computed(() => {
  return SUtils.createTextStyle(customisation.iconColor);
});

const textStyle = computed(() => {
  return SUtils.createTextStyle(customisation.textColor);
});

const closeIconStyle = computed(() => {
  return SUtils.createTextStyle(customisation.endLiveChatIconColor);
});

const handleEndChat = () => {
  emit('end-chat');
};
</script>

<template>
  <div class="input-radius live-chat-indicator shadow-hard" data-testid="live-chat-indicator" :style="containerStyle">
    <div class="live-chat-indicator__icon" :style="iconStyle">
      <v-icon name="MdAccountcircleRound" />
    </div>
    <div class="live-chat-indicator__text theme-font-main" data-testid="live-chat-indicator-text" :style="textStyle">
      {{ text }}
    </div>
    <button
      class="live-chat-indicator__close"
      data-testid="live-chat-end-button"
      :style="closeIconStyle"
      :disabled="disabled"
      aria-label="End live chat"
      @click="handleEndChat"
    >
      <v-icon name="MdClear" />
    </button>
  </div>
</template>

<style scoped lang="scss">
.live-chat-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  position: sticky;
  top: 0;
  z-index: 10;
}

.live-chat-indicator__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  width: 24px;
  height: 24px;

  svg {
    width: 100%;
    height: 100%;
  }
}

.live-chat-indicator__text {
  flex: 1;
}

.live-chat-indicator__close {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  transition: opacity 0.2s ease;

  width: 24px;
  height: 24px;

  svg {
    width: 100%;
    height: 100%;
  }

  &:hover:not(:disabled) {
    opacity: 0.7;
  }

  &:focus {
    outline: 2px solid currentColor;
    outline-offset: 2px;
    border-radius: 4px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
}
</style>
